import { API } from '../Api';

// Hiệu ứng tương tác trên live: người xem trả Ca bấm trò lên màn hình streamer, thầu khung tên.
// Streamer cài ở Cài đặt Donate tab Tương tác; overlay OBS ở /effect-screen/:id?key=.

const BASE = '/v1/payment-service/live-effect';
const PUBLIC = '/v1/payment-service/public/live-effect';

export type LiveEffectGroup = 'Overlay' | 'Obs';

export interface LiveEffectItem {
  code: string;
  name: string;
  group: LiveEffectGroup;
  enabled: boolean;
  priceCa: number;
  durationSec: number;
  cooldownSec: number;
  membersOnly: boolean;
}

export interface LiveBillboardPackage {
  hours: number;
  priceCa: number;
}

export interface LiveBillboardView {
  id: string;
  viewerName: string;
  message: string;
  hours: number;
  priceCa: number;
  status: 'Charging' | 'Active' | 'Finished' | 'Removed' | 'Failed';
  secondsLeft: number;
  createdAt: string;
  removedReason?: string | null;
}

export interface LiveEffectMine {
  isEnabled: boolean;
  isPaused: boolean;
  overlayKey: string;
  effects: LiveEffectItem[];
  obsSourceName?: string | null;
  obsMicName?: string | null;
  obsPenaltySceneName?: string | null;
  overlayOnline: boolean;
  obsOnline: boolean;
  billboardEnabled: boolean;
  billboardPosition: string;
  billboardPackages: LiveBillboardPackage[];
  activeBillboard?: LiveBillboardView | null;
  recentBillboards: LiveBillboardView[];
  emojis: string[];
  removeReasons: string[];
  minPriceCa: number;
  maxPriceCa: number;
}

export interface SaveLiveEffectBody {
  isEnabled: boolean;
  isPaused: boolean;
  effects: Pick<
    LiveEffectItem,
    'code' | 'enabled' | 'priceCa' | 'durationSec' | 'cooldownSec' | 'membersOnly'
  >[];
  obsSourceName?: string | null;
  obsMicName?: string | null;
  obsPenaltySceneName?: string | null;
  billboardEnabled: boolean;
  billboardPosition: string;
  billboardPackages: LiveBillboardPackage[];
}

export interface LiveEffectTriggerRow {
  id: string;
  viewerName: string;
  code: string;
  name: string;
  priceCa: number;
  status: 'Charging' | 'Queued' | 'Played' | 'Missed' | 'Failed';
  note?: string | null;
  createdAt: string;
}

export interface LiveEffectPublicItem {
  code: string;
  name: string;
  group: LiveEffectGroup;
  priceCa: number;
  durationSec: number;
  cooldownLeftSec: number;
  membersOnly: boolean;
}

export interface LiveBillboardActive {
  viewerName: string;
  message: string;
  secondsLeft: number;
  isMember?: boolean;
}

export interface LiveEffectPublic {
  available: boolean;
  obsOnline: boolean;
  memberDiscountPercent: number;
  /** Giá thấp nhất một lượt bấm; giá hội viên không xuống dưới mức này. */
  minPriceCa?: number;
  effects: LiveEffectPublicItem[];
  emojis: string[];
  billboard: {
    enabled: boolean;
    packages: LiveBillboardPackage[];
    active?: LiveBillboardActive | null;
  };
}

export interface LiveEffectEvent {
  kind: 'play' | 'reset' | 'billboard';
  triggerId?: string | null;
  code?: string | null;
  durationSec: number;
  viewerName?: string | null;
  emoji?: string | null;
  isMember?: boolean;
}

export interface LiveEffectOverlay {
  isEnabled: boolean;
  obsSourceName?: string | null;
  obsMicName?: string | null;
  obsPenaltySceneName?: string | null;
  enabledCodes: string[];
  queued: LiveEffectEvent[];
  billboard?: LiveBillboardActive | null;
  billboardPosition: string;
}

export const getMyLiveEffect = () =>
  API.get<LiveEffectMine>(`${BASE}/me`).then((r) => r.data);
export const saveLiveEffect = (body: SaveLiveEffectBody) =>
  API.post<LiveEffectMine>(`${BASE}/me`, body).then((r) => r.data);
export const regenerateLiveEffectKey = () =>
  API.post<LiveEffectMine>(`${BASE}/me/regenerate-key`).then((r) => r.data);
export const resetLiveEffectObs = () =>
  API.post(`${BASE}/me/reset-obs`).then((r) => r.data);
export const getMyLiveEffectTriggers = () =>
  API.get<LiveEffectTriggerRow[]>(`${BASE}/me/triggers`).then((r) => r.data);
export const removeLiveBillboard = (reason: string) =>
  API.post<LiveEffectMine>(`${BASE}/me/billboard/remove`, { reason }).then(
    (r) => r.data
  );

export const getPublicLiveEffect = (userId: string) =>
  API.get<LiveEffectPublic>(PUBLIC, { params: { userId } }).then((r) => r.data);
export const triggerLiveEffect = (body: {
  creatorId: string;
  code: string;
  priceCa: number;
  emoji?: string;
}) =>
  API.post<{ triggerId: string; balanceAfter: number }>(
    `${BASE}/trigger`,
    body
  ).then((r) => r.data);
export const buyLiveBillboard = (body: {
  creatorId: string;
  hours: number;
  priceCa: number;
  message: string;
}) =>
  API.post<LiveBillboardView>(`${BASE}/billboard`, body).then((r) => r.data);

export const getLiveEffectOverlay = (userId: string, key: string) =>
  API.get<LiveEffectOverlay>(`${PUBLIC}/overlay`, {
    params: { userId, key },
  }).then((r) => r.data);
export const pingLiveEffectOverlay = (
  userId: string,
  key: string,
  obsConnected: boolean
) =>
  API.post(
    `${PUBLIC}/overlay/ping`,
    { obsConnected },
    { params: { userId, key } }
  ).then((r) => r.data);
export const markLiveEffectPlayed = (
  userId: string,
  key: string,
  triggerId: string,
  note?: string
) =>
  API.post(
    `${PUBLIC}/overlay/played`,
    { triggerId, note },
    { params: { userId, key } }
  ).then((r) => r.data);
